import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, Save, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { Musikk, MusikkKategori, musikkAPI } from '@/services/api';
import EditSongTab, { EditSongTabRef } from '@/components/settings/EditSongTab';
import TranspositionsTab from '@/components/settings/TranspositionsTab';
import FilesTab from '@/components/settings/FilesTab';
import AppHeader from '@/components/layout/AppHeader';
import SideFeil from '@/components/common/SideFeil';
import { erIkkeFunnet } from '@/lib/erIkkeFunnet';

/** Hvorfor sangen ikke kunne lastes: 404/403 vises likt, alt annet kan prøves igjen. */
type Lastefeil = 'ikke-funnet' | 'annen';

export default function SongEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('edit');
  const [musikk, setMusikk] = useState<Musikk | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [kategorier, setKategorier] = useState<MusikkKategori[]>([]);
  const editSongTabRef = useRef<EditSongTabRef>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastefeil, setLastefeil] = useState<Lastefeil | null>(null);

  const loadMusikk = async () => {
    try {
      setLoading(true);
      setLastefeil(null);
      const response = await musikkAPI.getById(parseInt(id!));
      setMusikk(response.musikk);
    } catch (err) {
      console.error('Kunne ikke laste sang:', err);
      if (musikk?.id === Number(id)) {
        // Omlasting av sangen skjemaet viser (etter lagring): skjemaet blir stående
        setErrorMessage('Kunne ikke laste sang');
      } else {
        // Sangen med denne id-en ble aldri lastet: feilvisningen erstatter skjemaet
        setLastefeil(erIkkeFunnet(err) ? 'ikke-funnet' : 'annen');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadKategorier = async () => {
    try {
      const response = await musikkAPI.getAllKategorier();
      setKategorier(response.kategorier);
    } catch (err) {
      console.error('Kunne ikke laste kategorier:', err);
    }
  };

  useEffect(() => {
    loadKategorier();
    if (id) {
      loadMusikk();
    } else {
      // /musikk/ny: en feil fra en tidligere sang i samme komponent skal ikke henge igjen
      setLastefeil(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleBack = () => {
    navigate(-1); // Bruk browser history for å gå tilbake
  };

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-dismiss error message after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleSave = async () => {
    if (!editSongTabRef.current) return;

    // Validate form
    const validation = editSongTabRef.current.validate();
    if (!validation.isValid) {
      setErrorMessage(`Validering feilet: ${validation.errors.join(', ')}`);
      return;
    }

    try {
      setSaving(true);
      const formData = editSongTabRef.current.getFormData();

      // Opprett nye kategorier først, deretter map til IDs
      const kategori_ids: number[] = [];
      for (const catName of formData.categories) {
        const existingKat = kategorier.find((k) => k.navn === catName);
        if (existingKat) {
          kategori_ids.push(existingKat.id);
        } else {
          // Opprett ny kategori
          const response = await musikkAPI.createKategori({ navn: catName });
          kategori_ids.push(response.kategori.id);
        }
      }
      
      // Last kategorier på nytt for å inkludere nye kategorier
      await loadKategorier();
      // Last også kategorier i EditSongTab
      if (editSongTabRef.current) {
        await editSongTabRef.current.reloadKategorier();
      }

      // Prepare data for API
      const apiData = {
        tittel: formData.tittel,
        artist: formData.artist || undefined,
        album: formData.album || undefined,
        utgivelsesaar: formData.utgivelsesaar !== '' ? formData.utgivelsesaar : undefined,
        publisert: formData.publisert,
        kategori_ids,
        notater: formData.notater || undefined,
        lengde_minutter: formData.lengde_minutter !== '' ? formData.lengde_minutter : undefined,
        lengde_sekunder: formData.lengde_sekunder !== '' ? formData.lengde_sekunder : undefined,
        grunntone: formData.grunntone || undefined,
        toneart_type: formData.toneart_type || undefined,
        bpm: formData.bpm !== '' ? formData.bpm : undefined,
        takt: formData.takt || undefined,
        tekst_og_akkorder: formData.tekst_og_akkorder || undefined,
      };

      if (id) {
        // Update existing song
        await musikkAPI.update(parseInt(id), apiData);
        setSuccessMessage('Sangen ble oppdatert');
        // Reload data to get updated values
        await loadMusikk();
      } else {
        // Create new song
        const response = await musikkAPI.create(apiData);
        setSuccessMessage('Ny sang ble opprettet');
        // Navigate to the new song's edit page (no need to reload, URL will change)
        navigate(`/musikk/${response.musikk.id}`);
      }
    } catch (err: any) {
      console.error('Kunne ikke lagre sang:', err);
      setErrorMessage(err.response?.data?.error || 'Kunne ikke lagre sang');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <AppHeader />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-muted-foreground">Laster sang...</p>
          </div>
        </div>
      </div>
    );
  }

  // Sangen kunne ikke lastes: vis feilvisningen, ikke et tomt skjema som kan lagres som ny sang
  if (lastefeil) {
    const tilMusikkbiblioteket = { tekst: 'Til musikkbiblioteket', til: '/settings?tab=musikk' };
    return lastefeil === 'ikke-funnet' ? (
      <SideFeil
        tittel="Fant ikke sangen"
        tekst="Den finnes ikke, eller du har ikke tilgang til den."
        tilbake={tilMusikkbiblioteket}
      />
    ) : (
      <SideFeil
        tittel="Kunne ikke laste sangen"
        tekst="Prøv igjen om litt."
        tilbake={tilMusikkbiblioteket}
        onProvIgjen={() => {
          void loadMusikk();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <AppHeader />

      {/* Toast notifications */}
      {errorMessage && (
        <div className="fixed top-4 right-4 z-[100] max-w-md animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="flex items-center gap-2 p-3 sm:p-4 bg-destructive/95 backdrop-blur-sm border border-destructive shadow-lg rounded-lg text-white text-sm">
            <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
            <span className="flex-1 font-medium">{errorMessage}</span>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setErrorMessage(null)} 
              className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20 shrink-0 text-white"
            >
              <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
          </div>
        </div>
      )}
      {successMessage && (
        <div className="fixed top-4 right-4 z-[100] max-w-md animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="flex items-center gap-2 p-3 sm:p-4 bg-green-600/95 backdrop-blur-sm border border-green-500 shadow-lg rounded-lg text-white text-sm">
            <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
            <span className="flex-1 font-medium">{successMessage}</span>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setSuccessMessage(null)} 
              className="h-7 w-7 sm:h-8 sm:w-8 hover:bg-white/20 shrink-0 text-white"
            >
              <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Sticky Header */}
      <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-sm border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleBack}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-semibold truncate">
                {musikk?.tittel || 'Ny sang'}
              </h1>
              <p className="text-sm text-muted-foreground hidden sm:block truncate">
                {musikk?.artist || 'Ukjent artist'}
              </p>
            </div>
            
            <div className="flex gap-2 shrink-0">
              <Button 
                variant="outline" 
                onClick={handleBack}
                className="hidden sm:flex"
                disabled={saving}
              >
                Avbryt
              </Button>
              {activeTab === 'edit' && (
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Lagrer...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Lagre
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-4 md:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="edit">
              <span className="hidden sm:inline">Rediger sang</span>
              <span className="sm:hidden">Rediger</span>
            </TabsTrigger>
            <TabsTrigger value="transpositions">
              <span className="hidden sm:inline">Transponeringer</span>
              <span className="sm:hidden">Transponer</span>
            </TabsTrigger>
            <TabsTrigger value="files">
              <span className="hidden sm:inline">Filer</span>
              <span className="sm:hidden">Filer</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="mt-0">
            <EditSongTab ref={editSongTabRef} musikk={musikk} onSave={loadMusikk} />
          </TabsContent>

          <TabsContent value="transpositions" className="mt-0">
            <TranspositionsTab musikk={musikk} />
          </TabsContent>

          <TabsContent value="files" className="mt-0">
            <FilesTab musikk={musikk} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

